import React from 'react';
import { ArrowRightIcon, RefreshCwIcon } from 'lucide-react';
import { Band, PullQuote } from './Band';
import { gateFacts, modelIndependence, pipelineStages } from '../../data/site';
export function Pipeline() {
  return <Band dark id="pipeline" eyebrow="Scenario production" title="Every scenario is tested against the models before a candidate sees it." description="A trap only counts if a frontier model walks past it, and a clean case only counts if it stays clean. Each project is generated, constructed, then attacked by the current models." aside={<PullQuote dark data-id="element-286" data-mp="3jug3ja8u8z1g">
          What survives goes live. What stops surviving is pulled, and
          regenerated.
        </PullQuote>} data-id="element-285" data-mp="kc6qmazudvy6">
      <ol className="grid gap-4 lg:grid-cols-4" data-id="element-287" data-mp="3ssjmbysfvhpk">
        {pipelineStages.map((stage, i) => <li key={stage.stage} className="relative flex" data-id="element-288" data-mp="1d1x72qoy59xg" data-mpi={i}>
            <div className="flex w-full flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-id="element-289" data-mp="1d02hv06n6vnk">
              <div className="flex items-center justify-between" data-id="element-290" data-mp="182kfjsn4reoj">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-teal-light" data-id="element-291" data-mp="1y492g1g1i3wy">
                  {stage.stage}
                </span>
                <span className="rounded-full border border-white/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.1em] text-bone/50" data-id="element-292" data-mp="228qfzivt3kza">
                  {stage.engine}
                </span>
              </div>
              <h3 className="mt-5 font-display text-xl font-medium text-bone" data-id="element-293" data-mp="1ehl0j4ts2upi">
                {stage.title}
              </h3>
              <p className="mt-2.5 max-w-[38ch] text-[15px] leading-relaxed text-bone/60" data-id="element-294" data-mp="1jhfrt9hgtmb3">
                {stage.body}
              </p>
            </div>

            {i < pipelineStages.length - 1 ? <ArrowRightIcon className="absolute -right-3 top-1/2 hidden h-5 w-5 -translate-y-1/2 text-teal-light lg:block" aria-hidden="true" data-id="element-295" data-mp="1t6hchcyygrve" /> : null}
          </li>)}
      </ol>

      <div className="mt-4 flex items-center gap-4 rounded-2xl border border-dashed border-teal-light/40 bg-teal-light/[0.04] px-6 py-5" data-id="element-296" data-mp="10pn2bp1luj84">
        <RefreshCwIcon className="h-5 w-5 shrink-0 text-teal-light" aria-hidden="true" data-id="element-297" data-mp="snw4r4csssrc" />
        <p className="max-w-[80ch] text-[15px] leading-relaxed text-bone/75" data-id="element-298" data-mp="omkgb25nudkq">
          <span className="font-semibold text-bone" data-id="element-299" data-mp="12fjriryetlmh">Loop.</span> Every new
          frontier release re-runs the gate across the live library. Anything
          the model now resolves unprompted is deprecated and regenerated.
        </p>
      </div>

      {/* Model independence */}
      <div className="mt-20 border-t border-white/10 pt-16" data-id="element-300" data-mp="5p11tj0fo2qx">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-teal-light" data-id="element-301" data-mp="5qkj9cxtsjua">
          Model independence
        </p>
        <h3 className="mt-5 max-w-3xl font-display text-[30px] font-light leading-[1.1] tracking-tight text-bone sm:text-[40px]" data-id="element-302" data-mp="epfp2nwyjrx2">
          We're long model capability, not short it.
        </h3>

        <div className="mt-9 grid gap-10 lg:grid-cols-2 lg:gap-16" data-id="element-303" data-mp="1v5ijav3znwuu">
          <p className="max-w-[68ch] text-lg leading-relaxed text-bone/70" data-id="element-304" data-mp="1q433pikkyiju">
            The assistant inside the assessment is an instrument the candidate
            operates, not something Lentic resells. Swap it for any frontier
            model and the product works identically. There is no prompt library
            that constitutes the intellectual property and no fine-tune that
            constitutes the moat.
          </p>
          <PullQuote dark data-id="element-305" data-mp="3rq6zhilas5q8">
            As assistants get more fluent, more work is delegated, more output
            is plausible, and a confident wrong answer accepted without
            checking becomes more common and more expensive. The question of
            who stays accountable gets more urgent, not less.
          </PullQuote>
        </div>

        <dl className="mt-14 grid gap-x-10 gap-y-8 border-t border-white/10 pt-10 sm:grid-cols-2 lg:grid-cols-4" data-id="element-306" data-mp="3mxxg6leou3wx">
          {modelIndependence.map(item => <div key={item.label} data-id="element-307" data-mp="jaez78b6qc7w">
              <dt className="font-mono text-xs uppercase tracking-[0.14em] text-teal-light" data-id="element-308" data-mp="jbglk8xnil4x">
                {item.label}
              </dt>
              <dd className="mt-3 max-w-[18ch] font-display text-2xl font-light leading-tight text-bone" data-id="element-309" data-mp="2qrvpvhvdjpin">
                {item.value}
              </dd>
            </div>)}
        </dl>
      </div>

      <dl className="mt-20 grid gap-x-10 gap-y-8 border-t border-white/10 pt-10 sm:grid-cols-3" data-id="element-310" data-mp="2hvecs93g9hk9">
        {gateFacts.map(fact => <div key={fact.label} data-id="element-311" data-mp="39kgdokw7v4fl">
            <dt className="font-display text-3xl font-light text-bone" data-id="element-312" data-mp="39kpxtrukyhdd">
              {fact.value}
            </dt>
            <dd className="mt-2 max-w-[30ch] text-[15px] leading-snug text-bone/60" data-id="element-313" data-mp="35k5x6eassvmh">
              {fact.label}
            </dd>
          </div>)}
      </dl>

      <p className="mt-16 max-w-[80ch] text-sm leading-relaxed text-bone/50" data-id="element-314" data-mp="2vtba57pq9rzz">
        Claim discipline: we don't imply a security certification, publish
        model-defeat rates we can't reproduce, or describe the product as
        detecting AI use. The claim is narrow. Our scenarios are re-tested
        against current frontier models and deprecated when they stop working.
      </p>
    </Band>;
}